<!-- src/components/course/ConferenceForm.vue -->
<template>
	<div class="conference-form-overlay" @click.self="handleOverlayClick">
		<div class="conference-form" @click.stop>
			<div class="conference-form__header">
				<h3 class="conference-form__title">
					{{ isEdit ? 'Редактирование конференции' : 'Создание конференции' }}
				</h3>

				<button
					class="conference-form__close-btn"
					type="button"
					title="Закрыть"
					@click="handleCancel"
				>
					✕
				</button>
			</div>

			<form class="conference-form__form" @submit.prevent="handleSubmit">
				<div class="conference-form__field">
					<label class="conference-form__label">
						Название конференции <span class="required">*</span>
					</label>
					<input
						v-model="formData.title"
						class="conference-form__input"
						:class="{ 'conference-form__input--error': errors.title }"
						type="text"
						placeholder="Введите название конференции"
						maxlength="200"
						@input="markTouched"
						@blur="validateField('title')"
					/>
					<span v-if="errors.title" class="conference-form__error">
						{{ errors.title }}
					</span>
				</div>

				<div class="conference-form__field">
					<label class="conference-form__label">Описание</label>
					<textarea
						v-model="formData.description"
						class="conference-form__textarea"
						rows="4"
						placeholder="Опишите тему конференции"
						maxlength="2000"
						@input="markTouched"
					/>
					<span class="conference-form__hint">
						{{ (formData.description || '').length }}/2000
					</span>
				</div>

				<div class="conference-form__grid">
					<div class="conference-form__field">
						<label class="conference-form__label">
							Дата и время <span class="required">*</span>
						</label>
						<DateTimePicker
							v-model="formData.scheduled_at"
							:min-date="minDateTime"
							:error="!!errors.scheduled_at"
							placeholder="Выберите дату и время"
							@change="handleDateChange"
						/>
						<span v-if="errors.scheduled_at" class="conference-form__error">
							{{ errors.scheduled_at }}
						</span>
					</div>

					<div class="conference-form__field">
						<label class="conference-form__label">Макс. участников</label>
						<input
							v-model="formData.max_participants"
							class="conference-form__input"
							:class="{ 'conference-form__input--error': errors.max_participants }"
							type="number"
							min="1"
							max="10000"
							placeholder="Без ограничений"
							@input="handleMaxParticipantsInput"
							@blur="validateField('max_participants')"
						/>
						<span v-if="errors.max_participants" class="conference-form__error">
							{{ errors.max_participants }}
						</span>
					</div>
				</div>

				<div class="conference-form__field">
					<label class="conference-form__label">Тип конференции</label>
					<div class="conference-form__radio-group">
						<label class="conference-form__radio">
							<input
								type="radio"
								value="agora"
								v-model="formData.conference_type"
								@change="handleTypeChange"
							/>
							<span class="radio-label">
								<span class="radio-icon">🎥</span>
								Встроенная
							</span>
						</label>
					</div>
				</div>

				<div
					v-if="formData.conference_type === 'external'"
					class="conference-form__field"
				>
					<label class="conference-form__label">
						Ссылка на конференцию <span class="required">*</span>
					</label>
					<input
						v-model="formData.meeting_link"
						class="conference-form__input"
						:class="{ 'conference-form__input--error': errors.meeting_link }"
						type="url"
						placeholder="https://zoom.us/j/..."
						@input="markTouched"
						@blur="validateField('meeting_link')"
					/>
					<span v-if="errors.meeting_link" class="conference-form__error">
						{{ errors.meeting_link }}
					</span>
					<span v-else class="conference-form__hint">
						Поддерживаются: Zoom, Google Meet, Microsoft Teams и др.
					</span>
				</div>

				<div
					v-if="formData.conference_type === 'agora'"
					class="conference-form__field"
				>
					<div class="conference-form__agora-info">
						<span class="agora-badge">✓ Канал будет создан автоматически</span>
					</div>
				</div>

				<div class="conference-form__actions">
					<button
						type="button"
						class="conference-form__btn conference-form__btn--cancel"
						@click="handleCancel"
						:disabled="saving"
					>
						Отменить
					</button>

					<button
						type="submit"
						class="conference-form__btn conference-form__btn--submit"
						:disabled="saving || !isFormValid || !hasChanges"
						:title="submitButtonTitle"
					>
						<span v-if="saving" class="conference-form__spinner"></span>
						{{ saving ? 'Сохранение...' : isEdit ? 'Обновить' : 'Создать' }}
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import DateTimePicker from '@/components/ui/DateTimePicker.vue'

export default {
	name: 'ConferenceForm',

	components: {
		DateTimePicker,
	},

	props: {
		conference: {
			type: Object,
			default: null,
		},
		saving: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['conference-submit', 'cancel'],

	data() {
		return {
			isTouched: false,

			formData: {
				title: '',
				description: '',
				scheduled_at: '',
				meeting_link: '',
				max_participants: null,
				conference_type: 'agora',
			},

			errors: {
				title: '',
				scheduled_at: '',
				meeting_link: '',
				max_participants: '',
			},

			initialData: null,
		}
	},

	computed: {
		isEdit() {
			return !!this.conference?.id
		},

		minDateTime() {
			return new Date()
		},

		isFormValid() {
			// Проверяем наличие ошибок валидации
			const hasErrors = Object.values(this.errors).some((e) => e)
			if (hasErrors) return false

			// Проверяем обязательные поля
			if (!this.formData.title?.trim()) return false
			if (!this.formData.scheduled_at) return false

			// Проверяем ссылку для внешних конференций
			if (
				this.formData.conference_type === 'external' &&
				!this.formData.meeting_link?.trim()
			) {
				return false
			}

			return true
		},

		hasChanges() {
			if (!this.isEdit) {
				return this.isTouched
			}

			if (!this.initialData) return false

			// Нормализуем значения для сравнения
			const normalize = (val) => {
				if (val === null || val === undefined || val === '') return null
				if (typeof val === 'string') return val.trim()
				return val
			}

			return (
				normalize(this.formData.title) !== normalize(this.initialData.title) ||
				normalize(this.formData.description) !==
					normalize(this.initialData.description) ||
				normalize(this.formData.scheduled_at) !==
					normalize(this.initialData.scheduled_at) ||
				normalize(this.formData.meeting_link) !==
					normalize(this.initialData.meeting_link) ||
				this.normalizeNumber(this.formData.max_participants) !==
					this.normalizeNumber(this.initialData.max_participants) ||
				normalize(this.formData.conference_type) !==
					normalize(this.initialData.conference_type)
			)
		},

		submitButtonTitle() {
			if (!this.isFormValid) {
				return 'Заполните обязательные поля'
			}
			if (this.isEdit && !this.hasChanges) {
				return 'Нет изменений для сохранения'
			}
			return ''
		},
	},

	mounted() {
		document.body.style.overflow = 'hidden'
		document.body.classList.add('conference-form-modal-open')
		this.initializeForm()
	},

	beforeUnmount() {
		document.body.style.overflow = ''
		document.body.classList.remove('conference-form-modal-open')
	},

	methods: {
		/**
		 * Инициализация формы данными
		 */
		initializeForm() {
			if (this.conference) {
				const formValues = {
					title: this.conference.title?.trim() || '',
					description: this.conference.description?.trim() || '',
					scheduled_at: this.conference.scheduled_at
						? this.formatLocalDateTime(new Date(this.conference.scheduled_at))
						: '',
					meeting_link: this.conference.meeting_link?.trim() || '',
					max_participants: this.normalizeNumber(
						this.conference.max_participants
					),
					conference_type: this.conference.conference_type || 'agora',
				}

				this.formData = { ...formValues }
				this.initialData = { ...formValues }
			} else {
				// Установка даты по умолчанию - через час
				const defaultDate = new Date()
				defaultDate.setHours(defaultDate.getHours() + 1)
				defaultDate.setMinutes(0)
				defaultDate.setSeconds(0)
				defaultDate.setMilliseconds(0)
				this.formData.scheduled_at = this.formatLocalDateTime(defaultDate)
			}
		},

		/**
		 * Нормализация числового значения
		 * @param {any} value - входное значение
		 * @returns {number|null}
		 */
		normalizeNumber(value) {
			if (value === null || value === undefined || value === '') {
				return null
			}

			const num = parseInt(value, 10)

			if (isNaN(num) || num <= 0) {
				return null
			}

			return num
		},

		/**
		 * Нормализация строки (пустая строка -> null)
		 * @param {string} value
		 * @returns {string|null}
		 */
		normalizeString(value) {
			if (!value || typeof value !== 'string') {
				return null
			}

			const trimmed = value.trim()
			return trimmed.length > 0 ? trimmed : null
		},

		/**
		 * Форматирование даты для input[type="datetime-local"]
		 * @param {Date} date
		 * @returns {string}
		 */
		formatLocalDateTime(date) {
			if (!(date instanceof Date) || isNaN(date.getTime())) {
				return ''
			}

			const year = date.getFullYear()
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const day = String(date.getDate()).padStart(2, '0')
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')

			return `${year}-${month}-${day}T${hours}:${minutes}`
		},

		/**
		 * Валидация URL
		 * @param {string} url
		 * @returns {boolean}
		 */
		isValidUrl(url) {
			if (!url) return false

			try {
				const parsed = new URL(url)
				return ['http:', 'https:'].includes(parsed.protocol)
			} catch {
				return false
			}
		},

		/**
		 * Валидация отдельного поля
		 * @param {string} fieldName
		 */
		validateField(fieldName) {
			switch (fieldName) {
				case 'title':
					if (!this.formData.title?.trim()) {
						this.errors.title = 'Название обязательно'
					} else if (this.formData.title.trim().length < 3) {
						this.errors.title = 'Минимум 3 символа'
					} else if (this.formData.title.trim().length > 200) {
						this.errors.title = 'Максимум 200 символов'
					} else {
						this.errors.title = ''
					}
					break

				case 'scheduled_at':
					if (!this.formData.scheduled_at) {
						this.errors.scheduled_at = 'Укажите дату и время'
					} else {
						const selectedDate = new Date(this.formData.scheduled_at)
						const now = new Date()

						if (isNaN(selectedDate.getTime())) {
							this.errors.scheduled_at = 'Некорректная дата'
						} else if (selectedDate < now && !this.isEdit) {
							this.errors.scheduled_at = 'Дата не может быть в прошлом'
						} else {
							this.errors.scheduled_at = ''
						}
					}
					break

				case 'meeting_link':
					if (this.formData.conference_type === 'external') {
						if (!this.formData.meeting_link?.trim()) {
							this.errors.meeting_link = 'Ссылка обязательна для внешней конференции'
						} else if (!this.isValidUrl(this.formData.meeting_link)) {
							this.errors.meeting_link =
								'Введите корректный URL (https://...)'
						} else {
							this.errors.meeting_link = ''
						}
					} else {
						this.errors.meeting_link = ''
					}
					break

				case 'max_participants':
					const value = this.formData.max_participants

					if (value !== null && value !== '' && value !== undefined) {
						const num = parseInt(value, 10)

						if (isNaN(num)) {
							this.errors.max_participants = 'Введите число'
						} else if (num < 1) {
							this.errors.max_participants = 'Минимум 1 участник'
						} else if (num > 10000) {
							this.errors.max_participants = 'Максимум 10000 участников'
						} else {
							this.errors.max_participants = ''
						}
					} else {
						this.errors.max_participants = ''
					}
					break
			}
		},

		/**
		 * Валидация всей формы
		 * @returns {boolean}
		 */
		validateForm() {
			this.validateField('title')
			this.validateField('scheduled_at')
			this.validateField('meeting_link')
			this.validateField('max_participants')

			return !Object.values(this.errors).some((e) => e)
		},

		markTouched() {
			this.isTouched = true
		},

		/**
		 * Обработчик изменения max_participants
		 * Исправляет проблему с v-model.number возвращающим ""
		 */
		handleMaxParticipantsInput(event) {
			this.markTouched()

			const value = event.target.value

			// Если поле пустое - устанавливаем null
			if (value === '' || value === null || value === undefined) {
				this.formData.max_participants = null
			} else {
				const num = parseInt(value, 10)
				this.formData.max_participants = isNaN(num) ? null : num
			}
		},

		/**
		 * Обработчик изменения даты
		 */
		handleDateChange() {
			this.markTouched()
			this.validateField('scheduled_at')
		},

		/**
		 * Обработчик изменения типа конференции
		 */
		handleTypeChange() {
			this.markTouched()

			// Сбрасываем ошибку ссылки при смене типа
			if (this.formData.conference_type === 'agora') {
				this.errors.meeting_link = ''
			}
		},

		handleOverlayClick() {
			this.shakeForm()
		},

		shakeForm() {
			const form = this.$el.querySelector('.conference-form')
			if (form) {
				form.classList.add('shake')
				setTimeout(() => {
					form.classList.remove('shake')
				}, 500)
			}
		},

		/**
		 * Подготовка данных для отправки
		 * @returns {Object}
		 */
		prepareSubmitData() {
			const submitData = {
				title: this.normalizeString(this.formData.title),
				description: this.normalizeString(this.formData.description),
				conference_type: this.formData.conference_type || 'agora',
				max_participants: this.normalizeNumber(this.formData.max_participants),
				meeting_link: null,
				scheduled_at: null,
			}

			// Обработка ссылки
			if (submitData.conference_type === 'external') {
				submitData.meeting_link = this.normalizeString(
					this.formData.meeting_link
				)
			}

			// Обработка даты - отправляем локальное время как есть (без конвертации в UTC)
			// datetime-local возвращает строку в формате "YYYY-MM-DDTHH:MM"
			if (this.formData.scheduled_at) {
				// Просто передаём значение из input как есть
				submitData.scheduled_at = this.formData.scheduled_at
			}

			return submitData
		},

		handleSubmit() {
			if (this.saving) return

			// Валидация формы
			if (!this.validateForm()) {
				return
			}

			if (!this.isFormValid) return

			if (this.isEdit && !this.hasChanges) {
				return
			}

			const submitData = this.prepareSubmitData()

			// Финальная проверка обязательных полей
			if (!submitData.title) {
				this.errors.title = 'Название обязательно'
				return
			}

			if (!submitData.scheduled_at) {
				this.errors.scheduled_at = 'Укажите дату и время'
				return
			}

			if (
				submitData.conference_type === 'external' &&
				!submitData.meeting_link
			) {
				this.errors.meeting_link = 'Укажите ссылку на конференцию'
				return
			}

			console.log('📤 Отправка данных конференции:', submitData)

			this.$emit('conference-submit', submitData)
		},

		handleCancel() {
			if (!this.hasChanges) {
				this.$emit('cancel')
				return
			}

			if (
				confirm(
					'Есть несохранённые данные. Вы действительно хотите закрыть форму?'
				)
			) {
				this.$emit('cancel')
			}
		},
	},
}
</script>

<style scoped>
.conference-form-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
	box-sizing: border-box;
	background: rgba(0, 0, 0, 0.55);
	backdrop-filter: blur(4px);
	overflow-y: auto;
	overscroll-behavior: contain;
}

.conference-form {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
	/* Учитываем padding оверлея, чтобы модалка не "резалась" по высоте при zoom */
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	width: 100%;
	max-width: 600px;
	margin: auto;
}

/* Адаптация под разные разрешения */
@media (max-width: 1366px) {
	.conference-form {
		max-height: calc(100vh - 40px);
		padding: 10px;
		max-width: 540px;
		overflow-y: auto;
	}

	.conference-form__header {
		margin-bottom: 6px;
		padding-bottom: 4px;
	}

	.conference-form__title {
		font-size: 15px;
	}

	.conference-form__close-btn {
		width: 24px;
		height: 24px;
		font-size: 16px;
	}

	.conference-form__form {
		gap: 6px;
	}

	.conference-form__field {
		gap: 3px;
	}

	.conference-form__label {
		font-size: 11px;
	}

	.conference-form__input,
	.conference-form__textarea {
		padding: 6px 10px;
		font-size: 11px;
		min-height: 32px;
	}

	.conference-form__textarea {
		min-height: 45px;
	}

	.conference-form__grid {
		gap: 6px;
	}

	.conference-form__actions {
		margin-top: 0;
		padding-top: 6px;
	}

	.conference-form__btn {
		padding: 6px 12px;
		font-size: 11px;
	}

	.conference-form__radio .radio-label {
		padding: 6px 10px;
		font-size: 11px;
	}

	.radio-icon {
		font-size: 14px;
	}

	.conference-form__agora-info {
		padding: 6px;
	}

	.agora-badge {
		font-size: 11px;
	}

	.conference-form__hint,
	.conference-form__error {
		font-size: 10px;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.conference-form {
		max-height: calc(100vh - 40px);
		padding: 14px;
		overflow-y: auto;
	}

	.conference-form__header {
		margin-bottom: 10px;
		padding-bottom: 8px;
	}

	.conference-form__title {
		font-size: 18px;
	}

	.conference-form__close-btn {
		width: 28px;
		height: 28px;
		font-size: 20px;
	}

	.conference-form__form {
		gap: 10px;
	}

	.conference-form__field {
		gap: 5px;
	}

	.conference-form__label {
		font-size: 12px;
	}

	.conference-form__input,
	.conference-form__textarea {
		padding: 8px 12px;
		font-size: 12px;
	}

	.conference-form__textarea {
		min-height: 55px;
	}

	.conference-form__grid {
		gap: 10px;
	}

	.conference-form__actions {
		margin-top: 4px;
		padding-top: 10px;
	}

	.conference-form__btn {
		padding: 8px 18px;
		font-size: 12px;
	}

	.conference-form__radio .radio-label {
		padding: 8px 14px;
		font-size: 12px;
	}

	.radio-icon {
		font-size: 16px;
	}

	.conference-form__agora-info {
		padding: 10px;
	}

	.agora-badge {
		font-size: 12px;
	}
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.conference-form {
		max-height: calc(100vh - 40px);
		padding: 16px;
		overflow-y: auto;
	}

	.conference-form__header {
		margin-bottom: 14px;
		padding-bottom: 10px;
	}

	.conference-form__title {
		font-size: 19px;
	}

	.conference-form__form {
		gap: 12px;
	}

	.conference-form__field {
		gap: 6px;
	}

	.conference-form__label {
		font-size: 13px;
	}

	.conference-form__input,
	.conference-form__textarea {
		padding: 9px 13px;
		font-size: 13px;
	}

	.conference-form__textarea {
		min-height: 60px;
	}

	.conference-form__grid {
		gap: 12px;
	}

	.conference-form__actions {
		padding-top: 12px;
	}

	.conference-form__btn {
		padding: 9px 20px;
		font-size: 13px;
	}

	.conference-form__radio .radio-label {
		padding: 9px 16px;
		font-size: 13px;
	}

	.conference-form__agora-info {
		padding: 12px;
	}
}

/* Анимация "потряхивания" */
.conference-form.shake {
	animation: shake 0.5s ease-in-out;
}

@keyframes shake {
	0%,
	100% {
		margin-left: 0;
		margin-right: 0;
	}
	10%,
	30%,
	50%,
	70%,
	90% {
		margin-left: -5px;
		margin-right: 5px;
	}
	20%,
	40%,
	60%,
	80% {
		margin-left: 5px;
		margin-right: -5px;
	}
}

.conference-form__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding-bottom: 16px;
	border-bottom: 2px solid #e8e8e8;
}

.conference-form__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 22px;
	font-weight: 600;
	margin: 0;
}

.conference-form__close-btn {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border: none;
	border-radius: 50%;
	color: #636363;
	font-size: 24px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.conference-form__close-btn:hover {
	background-color: #f5f5f5;
	color: #dc3545;
}

.conference-form__form {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.conference-form__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.conference-form__label {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
}

.required {
	color: #dc3545;
}

.conference-form__input,
.conference-form__textarea {
	padding: 12px 16px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	color: #252641;
	transition: all 0.2s ease;
	background-color: #fff;
}

.conference-form__input:focus,
.conference-form__textarea:focus {
	outline: none;
	border-color: #0095ec;
	box-shadow: 0 0 0 3px rgba(0, 149, 236, 0.1);
}

/* Состояние ошибки */
.conference-form__input--error {
	border-color: #dc3545;
}

.conference-form__input--error:focus {
	border-color: #dc3545;
	box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.conference-form__error {
	color: #dc3545;
	font-size: 12px;
	font-family: 'Poppins', Helvetica;
}

.conference-form__hint {
	color: #888;
	font-size: 12px;
	font-family: 'Poppins', Helvetica;
}

.conference-form__textarea {
	resize: vertical;
	min-height: 80px;
}

/* Улучшенный datetime input */
.conference-form__datetime-wrapper {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.conference-form__input--datetime {
	cursor: pointer;
}

.conference-form__input--datetime::-webkit-calendar-picker-indicator {
	cursor: pointer;
	padding: 4px;
	margin-right: -4px;
	border-radius: 4px;
	transition: background-color 0.2s ease;
}

.conference-form__input--datetime::-webkit-calendar-picker-indicator:hover {
	background-color: rgba(0, 149, 236, 0.1);
}

.conference-form__datetime-hint {
	color: #888;
	font-size: 12px;
	font-style: italic;
}

.conference-form__grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 20px;
}

.conference-form__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
	margin-top: 8px;
	padding-top: 20px;
	border-top: 2px solid #e8e8e8;
}

.conference-form__btn {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.conference-form__btn:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

.conference-form__btn--cancel {
	background-color: #f5f5f5;
	color: #636363;
}

.conference-form__btn--cancel:hover:not(:disabled) {
	background-color: #e8e8e8;
}

.conference-form__btn--submit {
	background-color: #0095ec;
	color: #ffffff;
}

.conference-form__btn--submit:hover:not(:disabled) {
	background-color: #0077be;
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.3);
}

/* Спиннер для кнопки */
.conference-form__spinner {
	width: 16px;
	height: 16px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: #fff;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Radio группа для типа конференции */
.conference-form__radio-group {
	display: flex;
	gap: 16px;
	flex-wrap: wrap;
}

.conference-form__radio {
	display: flex;
	align-items: center;
	cursor: pointer;
}

.conference-form__radio input[type='radio'] {
	display: none;
}

.conference-form__radio .radio-label {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 20px;
	border: 2px solid #e8e8e8;
	border-radius: 8px;
	transition: all 0.2s ease;
	font-size: 14px;
	color: #636363;
	background-color: #fff;
}

.conference-form__radio input[type='radio']:checked + .radio-label {
	border-color: #0095ec;
	background-color: rgba(0, 149, 236, 0.05);
	color: #0095ec;
}

.conference-form__radio:hover .radio-label {
	border-color: #0095ec;
}

.radio-icon {
	font-size: 18px;
}

/* Agora info */
.conference-form__agora-info {
	padding: 16px;
	background: linear-gradient(135deg, #e8f5e9 0%, #e3f2fd 100%);
	border-radius: 8px;
	border: 1px solid #c8e6c9;
}

.agora-badge {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: #2e7d32;
	font-weight: 500;
	font-size: 14px;
}

/* DateTimePicker телепортируется в body и при некоторых zoom/высотах экрана может "резаться".
   Ограничиваем высоту дропдауна и даём внутренний скролл только пока открыта эта модалка. */
:global(body.conference-form-modal-open .datetime-picker__dropdown) {
	max-height: min(480px, calc(100vh - 16px));
	display: flex;
	flex-direction: column;
}

:global(body.conference-form-modal-open .datetime-picker__content) {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}

:global(body.conference-form-modal-open .datetime-picker__footer) {
	flex-shrink: 0;
}
</style>
